import React, { useState, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/core';

interface ColorPickerProps {
  editor: Editor;
  type: 'color' | 'highlight';
  palette?: string[];
}

const DEFAULT_COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
  '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
];

const DEFAULT_HIGHLIGHTS = [
  '#fef08a', '#bbf7d0', '#a5f3fc', '#bfdbfe', '#ddd6fe', '#fbcfe8', '#fecaca', '#fed7aa',
];

export const ColorPicker: React.FC<ColorPickerProps> = ({ editor, type, palette }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const colors = palette || (type === 'color' ? DEFAULT_COLORS : DEFAULT_HIGHLIGHTS);

  const currentColor = type === 'color'
    ? editor.getAttributes('textStyle').color
    : editor.getAttributes('highlight').color;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColorSelect = (color: string) => {
    if (type === 'color') {
      if (color === currentColor) {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
      }
    } else {
      if (color === currentColor) {
        editor.chain().focus().unsetHighlight().run();
      } else {
        editor.chain().focus().setHighlight({ color }).run();
      }
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    if (type === 'color') {
      editor.chain().focus().unsetColor().run();
    } else {
      editor.chain().focus().unsetHighlight().run();
    }
    setIsOpen(false);
  };

  return (
    <div className="color-picker-wrapper" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={currentColor ? 'has-color' : ''}
        title={type === 'color' ? 'Text Color' : 'Highlight Color'}
      >
        <span
          className="color-indicator"
          style={{
            borderBottomColor: type === 'color' ? (currentColor || '#000') : undefined,
            backgroundColor: type === 'highlight' ? (currentColor || '#ffff00') : undefined,
          }}
        >
          {type === 'color' ? 'A' : 'H'}
        </span>
      </button>
      {isOpen && (
        <div className="color-picker-dropdown">
          <div className="color-grid">
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                className={`color-swatch ${color === currentColor ? 'is-selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorSelect(color)}
                title={color}
              />
            ))}
          </div>
          <button type="button" className="clear-color" onClick={handleClear}>
            Clear {type === 'color' ? 'Color' : 'Highlight'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
